import { baseApi } from "../base-api"

// Define API response types
export interface Vendor {
  name: string
  active: boolean
  phoneNo: string
  ntn: string
  cnic: string
  filerStatus: string
  bankAccount: string
  city: string
  description: string
  documentAttachment: string
  additionalInformation: string
  createdBy: string
  id: string
  secondaryId?: number
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

interface CreateVendorParams {
  name: string
  active: boolean
  phoneNo: string
  ntn: string
  cnic: string
  filerStatus: string
  bankAccount: string
  city: string
  description: string
  documentAttachment: string
  additionalInformation: string
}

interface CreateVendorResponse {
  data: Vendor
  meta: null
  msg: string
  success: boolean
}
interface GetVendorParams {
  id: string
}
interface GetVendorResponse {
  data: Vendor
  meta: null
  msg: string
  success: boolean
}

interface GetVendorsParams {
  page?: number
  limit?: number
  name?: string
  active?: boolean
  phoneNo?: string
  ntn?: string
  cnic?: string
  filerStatus?: undefined | null | "FILER" | "NON_FILER"
  city?: string
}

interface GetVendorsResponse {
  data: Vendor[]
  meta: null
  msg: string
  success: boolean
}

export type UpdateVendorParams = Omit<Vendor, "createdAt" | "createdBy" | "updatedAt" | "deletedAt"> & {
  id: string
}

interface UpdateVendorResponse {
  data: Vendor | null
  meta: null
  msg: string
  success: boolean
}

interface DeleteVendorParams {
  id: string
}

interface DeleteVendorResponse {
  success: boolean
  message: string
}

// API slice for vendor module
export const vendorApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get all vendors with pagination and search
    getVendors: builder.query<GetVendorsResponse, GetVendorsParams>({
      query: params => {
        return {
          url: `vendors`,
          method: "GET",
          params
        }
      },
      providesTags: ["Vendor"]
    }),

    // Get single vendor by ID
    getVendor: builder.query<GetVendorResponse, GetVendorParams>({
      query: id => ({
        url: `vendors/${id}`,
        method: "GET"
      }),
      providesTags: ["Vendor"]
    }),

    // Create new vendor
    createVendor: builder.mutation<CreateVendorResponse, CreateVendorParams>({
      query: vendor => ({
        url: "vendors",
        method: "POST",
        body: vendor
      }),
      invalidatesTags: ["Vendor"]
    }),

    // Update existing vendor
    updateVendor: builder.mutation<UpdateVendorResponse, UpdateVendorParams>({
      query: ({ id, ...vendor }) => ({
        url: `vendors/${id}`,
        method: "PATCH",
        body: vendor
      }),
      invalidatesTags: ["Vendor"]
    }),

    // Delete vendor
    deleteVendor: builder.mutation<DeleteVendorResponse, DeleteVendorParams>({
      query: ({ id }) => ({
        url: `vendors/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Vendor"]
    })
  })
})

// Export hooks for use in components
export const {
  useGetVendorsQuery,
  useLazyGetVendorsQuery,
  useGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation
} = vendorApi
